import { Module } from "vuex";
import { rootState } from "../types/store/state";
import {
  fetchDashboards,
  fetchDashboardById,
  createDashboard,
  updateDashboard,
  updateDashboardWidgets,
  deleteDashboard,
} from "@/services/dashboardService";

export interface DashboardSummary {
  id: number;
  name: string;
  createdAt?: string;
}

export interface DashboardsState {
  list: DashboardSummary[];
  byId: Record<string, any>;
  loading: boolean;
  lastFetchTs: number;
}

export const dashboards: Module<DashboardsState, rootState> = {
  namespaced: true,

  state: (): DashboardsState => ({
    list: [],
    byId: {},
    loading: false,
    lastFetchTs: 0,
  }),

  getters: {
    dashboards: (s) => s.list,
    dashboardById: (s) => (id: string | number) => s.byId[id],
    loading: (s) => s.loading,
  },

  mutations: {
    setLoading(state, val: boolean) {
      state.loading = val;
    },
    setLastFetchTs(state, ts: number) {
      state.lastFetchTs = ts;
    },
    setList(state, dashboards: DashboardSummary[]) {
      state.list = dashboards;
      dashboards.forEach((d) => (state.byId[d.id] = d));
    },
    cacheOne(state, dash: any) {
      state.byId[dash.id] = dash;
    },
    upsert(state, dash: DashboardSummary) {
      const i = state.list.findIndex((d) => d.id === dash.id);
      if (i === -1) state.list.unshift(dash);
      else state.list.splice(i, 1, dash);
      state.byId[dash.id] = dash;
    },
    remove(state, id: number) {
      state.list = state.list.filter((d) => d.id !== id);
      delete state.byId[id];
    },
  },

  actions: {
    async loadAll({ state, commit }, force = false) {
      if (!force && (state.loading || Date.now() - state.lastFetchTs < 30_000)) return;
      commit("setLoading", true);
      try {
        const data = await fetchDashboards();
        commit("setList", data);
        commit("setLastFetchTs", Date.now());
      } finally {
        commit("setLoading", false);
      }
    },

    async loadOne({ state, commit }, id: number) {
      if (state.byId[id]) return state.byId[id];
      const dash = await fetchDashboardById(id);
      commit("cacheOne", dash);
      commit("upsert", { id: dash.id, name: dash.name, createdAt: dash.createdAt });
      return dash;
    },

    async save({ commit, state }, { id, name, widgets, companyId }) {
      let dash;
      let dashId = id;
      if (id) {
        await updateDashboard(id, { title: name });
        dash = { id, name, createdAt: state.byId[id]?.createdAt ?? new Date().toISOString() };
      } else {
        dash = await createDashboard({ title: name, companyId });
        dashId = dash.id;
        dash.createdAt = new Date().toISOString();
      }
      if (widgets?.length) await updateDashboardWidgets(dashId!, widgets);
      commit("upsert", dash);
      return dashId;
    },

    async remove({ commit }, id: number) {
      await deleteDashboard(id);
      commit("remove", id);
    },
  },
};
